import type { ToolApproval } from "@/api/models"

import { Check, Clock, Hourglass, X } from "@/components/icons"
import { Badge, type BadgeProps } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

type ToolApprovalStatus = ToolApproval["status"]

const statusConfig: Record<
  ToolApprovalStatus,
  { label: string; icon: typeof Check; variant: BadgeProps["variant"] }
> = {
  pending: {
    label: "Pending",
    icon: Hourglass,
    variant: "warning",
  },
  approved: {
    label: "Approved",
    icon: Check,
    variant: "success",
  },
  denied: {
    label: "Denied",
    icon: X,
    variant: "destructive",
  },
  expired: {
    label: "Expired",
    icon: Clock,
    variant: "disabled",
  },
  cancelled: {
    label: "Cancelled",
    icon: X,
    variant: "disabled",
  },
}

type ToolApprovalStatusBadgeProps = {
  status: ToolApprovalStatus
  className?: string
  showIcon?: boolean
  size?: "sm" | "default"
}

export function ToolApprovalStatusBadge({
  status,
  className,
  showIcon = true,
  size = "sm",
}: ToolApprovalStatusBadgeProps) {
  const config = statusConfig[status]
  const Icon = config.icon
  const iconSize = size === "sm" ? "size-3" : "size-4"
  const textSize = size === "sm" ? "text-xs" : "text-sm"

  return (
    <Badge
      className={cn("inline-flex items-center gap-1 font-medium shadow-none", textSize, className)}
      variant={config.variant}
    >
      {showIcon && <Icon className={iconSize} />}
      {config.label}
    </Badge>
  )
}
